export type FieldValue =
  | Record<string, string>
  | boolean
  | null
  | number
  | string
  | string[]
  | undefined;

type FieldValidatorVerifyResult =
  | Promise<string | boolean | undefined>
  | string
  | boolean;

type FieldValidatorVerify<P> = (
  fieldValue?: FieldValue,
  otherFieldsValues?: Record<string, FieldValue>,
  props?: P
) => FieldValidatorVerifyResult;

export type FieldValidator<P> = {
  verify: FieldValidatorVerify<P>;
  message?: string;
};

type ValidationErrorMessage = string | undefined;

export const buildValidator = <P>(
  verifyFunc: FieldValidatorVerify<P>,
  message?: string
) => {
  return { verify: verifyFunc, message };
};

export const maxLength = (length: number, message?: string) =>
  buildValidator(
    value => !value || (typeof value === 'string' && value.length <= length),
    message
  );

export const minLength = (length: number, message?: string) =>
  buildValidator(
    value => !value || (typeof value === 'string' && value.length >= length),
    message
  );

export const isRequired = (message?: string) =>
  buildValidator(value => !!value, message);

export const isTrue = (message?: string) =>
  buildValidator(value => value === true, message);

const isValidationError = (validationValue?: FieldValidatorVerifyResult) =>
  !validationValue || typeof validationValue === 'string';

const getValidationErrorMessage = <P>(
  validator: FieldValidator<P>,
  validationValue?: FieldValidatorVerifyResult
) =>
  typeof validationValue === 'string' ? validationValue : validator.message;

/**
 * A special validator that will return false a soon as
 * any of its validator reports an error
 *
 * @param {Array} validators an array of validators
 * @return {Object} A validator
 */
export const failFast = <P>(...validators: FieldValidator<P>[]) =>
  buildValidator<P>(async (value, values, props) => {
    const promises = [];
    // Running validator in sequence
    for (const validator of validators) {
      const validationValue = validator.verify(value, values, props);
      // Failing fast in case of error
      if (isValidationError(validationValue)) {
        return getValidationErrorMessage(validator, validationValue);
      }
      promises.push(validationValue);
    }
    // No error was detected so far, there still might be in asynchronous validators
    // Let's wait for all promise to resolve
    const results = await Promise.all(promises);
    for (const result of results) {
      if (isValidationError(result)) {
        return getValidationErrorMessage(
          validators[results.indexOf(result)],
          result
        );
      }
    }
    return true; // No errors at all, returning true
  });

/**
 *
 * @param {RegExp} regex Regular expression
 * @param {string}  message Error message
 * @return {Object} A validator object
 */
export const matchRegex = (regex: RegExp, message: string) =>
  buildValidator(
    value => typeof value === 'string' && regex.test(value),
    message
  );

/**
 * Validate the passed value against each passed validators.
 *
 * @param {Array} fieldValidators an array of validators
 * @param {*} fieldValue A value to test against each validator
 * @param {*} otherFieldsValues The form values in case we have to validate our field against the other (for example a password and its confirmation)
 * @param {Object} props The form props
 * @return {Promise} a Promise resolving with an Array (size of fieldValidators)
 */
export const validateField = <P>(
  fieldValidators: FieldValidator<P>[] = [],
  fieldValue: FieldValue,
  otherFieldsValues?: Record<string, FieldValue>,
  props?: P
) => {
  // Call every validator with given value
  const promises = fieldValidators.map(fieldValidator =>
    fieldValidator.verify(fieldValue, otherFieldsValues, props)
  );
  return Promise.all(promises).then(results =>
    results.reduce<Array<ValidationErrorMessage>>(
      (errors, validationResult, index) => {
        if (isValidationError(validationResult)) {
          errors.push(
            getValidationErrorMessage(fieldValidators[index], validationResult)
          );
        }
        return errors;
      },
      []
    )
  );
};

/**
 * Validate all fields (identified by the keys of fieldsValidators) against matching validators
 *
 * @example
 * const fieldsValidators = {
 *    password: [isRequired('it is required'), minLength(50, 'too short')]
 * }
 *
 * const fieldsValues = {
 *    password: {
 *      value: '12345'
 *    }
 * }
 *
 * const result = await validateAll(fieldsValidators, fieldsValues);
 * // result.password === [true, 'too short']
 *
 * @param {Object} fieldsValidators An Object where keys are fields name
 * @param {Array} fieldsValidators.fieldName An array of validators for field named fieldName
 * @param {Object} fieldsValues An Object where keys are fields name
 * @param {Object} fieldsValues.fieldName An object representing a field called fieldName
 * @param {*} fieldsValues.fieldName.value The value of the field
 * @return {Promise} A promise resolving with an Object like fieldValidators where only fields with errors are included
 */
export const validateAll = <P>(
  fieldsValidators: Record<string, Array<FieldValidator<P>>>,
  fieldsValues: { [key: string]: { value: FieldValue } }
) => {
  const promises = [];
  const fieldsNames: string[] = [];
  for (const [field, fieldValidators] of Object.entries(fieldsValidators)) {
    // Saving field name for later use
    fieldsNames.push(field);
    // Building an array of Promises as returned by validateField
    // $FlowFixMe entries as a return type of Array<[string, mixed]> while it should be Array<[string, T]>
    promises.push(validateField(fieldValidators, fieldsValues[field].value));
  }
  return Promise.all(promises).then(results => {
    return results.reduce<Record<string, Array<ValidationErrorMessage>>>(
      (resultsToReturn, result, index) => {
        if (result && result.length) {
          resultsToReturn[fieldsNames[index]] = result;
        }
        return resultsToReturn;
      },
      {}
    );
  });
};
